import { useCallback, useEffect, useState } from 'react';
import { ConfirmProvider } from '../components/ui/ConfirmDialog';
import { Icon } from '../components/ui/Icon';
import { platformLogout, platformMe, type PlatformUser } from './api';
import { PlatformLoginPage } from './PlatformLoginPage';
import { PlatformHome } from './PlatformHome';
import { BusinessDetailPage } from './BusinessDetailPage';
import { PlatformInvitationsPage } from './PlatformInvitationsPage';

// Fase 25B: shell del control plane, SEPARADO del tenant. Vive bajo el hash #platform/* y tiene su
// propia autenticacion (cookie de plataforma) e identidad visual "Administración de plataforma". No
// aparece en la navegacion tenant; se llega por ruta directa.
// ONB-010: #platform/invitations — invitaciones a la beta privada.

type Route = { name: 'home' } | { name: 'business'; id: string } | { name: 'invitations' };

function parseRoute(): Route {
  const raw = window.location.hash.replace(/^#/, '');
  const seg = raw.split('/').filter(Boolean); // ['platform', ...]
  if (seg[1] === 'businesses' && seg[2]) return { name: 'business', id: seg[2] };
  if (seg[1] === 'invitations') return { name: 'invitations' };
  return { name: 'home' };
}

export function navigatePlatform(path: string) {
  window.location.hash = path.startsWith('#') ? path : `#${path}`;
}

export function PlatformApp() {
  const [status, setStatus] = useState<'loading' | 'authed' | 'anon'>('loading');
  const [user, setUser] = useState<PlatformUser | null>(null);
  const [route, setRoute] = useState<Route>(parseRoute());

  useEffect(() => {
    const onHash = () => setRoute(parseRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const { user: u } = await platformMe();
      setUser(u); setStatus('authed');
    } catch {
      setUser(null); setStatus('anon');
    }
  }, []);

  useEffect(() => { void loadMe(); }, [loadMe]);

  const onLoggedIn = (u: PlatformUser) => { setUser(u); setStatus('authed'); navigatePlatform('#platform'); };

  const logout = async () => {
    try { await platformLogout(); } catch { /* noop */ }
    setUser(null); setStatus('anon');
    navigatePlatform('#platform');
  };

  if (status === 'loading') {
    return <div className="platform-shell platform-shell--center"><span className="spinner" /> Cargando…</div>;
  }
  if (status === 'anon' || !user) {
    return <ConfirmProvider><PlatformLoginPage onLoggedIn={onLoggedIn} /></ConfirmProvider>;
  }

  return (
    <ConfirmProvider>
      <div className="platform-shell">
        <header className="platform-topbar">
          <button className="platform-brand" onClick={() => navigatePlatform('#platform')} type="button">
            <span className="platform-brand__name">Negocio360</span>
            <span className="platform-brand__tag">Administración de plataforma</span>
          </button>
          <div className="platform-topbar__right">
            <span className="platform-topbar__user"><Icon name="users" size={16} /> {user.displayName}</span>
            <button className="button button--ghost" onClick={() => void logout()} type="button"><Icon name="logout" size={16} /> Salir</button>
          </div>
        </header>
        <main className="platform-main">
          {route.name === 'home' && <PlatformHome />}
          {route.name === 'business' && <BusinessDetailPage businessId={route.id} />}
          {route.name === 'invitations' && <PlatformInvitationsPage />}
        </main>
      </div>
    </ConfirmProvider>
  );
}
